import React, { useEffect, useState, useRef } from 'react';
import adv1 from '../assets/big-blue-advantage/adv1.png';
import adv2 from '../assets/big-blue-advantage/adv2.png';
import adv3 from '../assets/big-blue-advantage/adv3.png';
import adv4 from '../assets/big-blue-advantage/adv4.png';
import adv5 from '../assets/big-blue-advantage/adv5.png';
import adv6 from '../assets/big-blue-advantage/adv6.png';
import adv7 from '../assets/big-blue-advantage/adv7.png';

export default function BigBlueAdvantage() {
  const [isMobile, setIsMobile] = useState(false);
  const [typedText, setTypedText] = useState('');
  const [headingVisible, setHeadingVisible] = useState(false);
  const fullText = 'The Big Blue Advantage';
  const headingRef = useRef(null);
  const contentRefs = useRef([]);
  const [visibleItems, setVisibleItems] = useState([]);

  const advantages = [
    { icon: adv1, title: 'Established in 2011.', description: '13+ years of experience in Forwarding, Logistics & Customs regulations.' },
    { icon: adv2, title: 'Global Service', description: 'Provider with HQ in Canada.' },
    { icon: adv3, title: 'Worldwide Coverage', description: 'By an exclusive network of world-class agents.' },
    { icon: adv4, title: 'Financial Strength', description: 'To take on demanding accounts.' },
    { icon: adv5, title: 'IT Solutions', description: 'As per latest market trend.' },
    { icon: adv6, title: '24/7 back up', description: 'Response team available to handle urgent & time sensitive matters.' },
    { icon: adv7, title: 'Cross Border', description: 'FTL, LTL freight services Worldwide.' }
  ];

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Heading observer for typing
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setHeadingVisible(true),
      { threshold: 0.3 }
    );
    if (headingRef.current) observer.observe(headingRef.current);
    return () => headingRef.current && observer.unobserve(headingRef.current);
  }, []);

  useEffect(() => {
    if (headingVisible && typedText.length < fullText.length) {
      const timeout = setTimeout(() => {
        setTypedText(fullText.slice(0, typedText.length + 1));
      }, 60);
      return () => clearTimeout(timeout);
    }
  }, [typedText, headingVisible]);

  // Fade/slide-in for content only
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const index = Number(entry.target.getAttribute('data-index'));
          if (entry.isIntersecting && !visibleItems.includes(index)) {
            setVisibleItems((prev) => [...prev, index]);
          }
        });
      },
      { threshold: 0.3 }
    );
    contentRefs.current.forEach((ref) => ref && observer.observe(ref));
    return () => {
      contentRefs.current.forEach((ref) => ref && observer.unobserve(ref));
    };
  }, [visibleItems]);

  return (
    <section className="w-full flex justify-center">
      <style>
        {`
          @keyframes fadeSlideUp {
            from { opacity: 0; transform: translateY(30px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fade-slide-up {
            animation: fadeSlideUp 0.8s ease-out forwards;
          }
        `}
      </style>

      <div className="w-full px-4 lg:w-[1200px] 2xl:w-[1600px] lg:ml-12 lg:mr-12 grid grid-cols-1 lg:grid-cols-2 [grid-template-rows:repeat(3,_850px)] lg:[grid-template-rows:700px_775px_675px] border-t border-black">
        
        {/* Sticky Left Column */}
        <div className="lg:sticky top-0 lg:row-span-3 lg:h-screen flex items-center justify-center px-6 border-black">
          <div
            ref={(el) => (contentRefs.current[0] = el)}
            data-index={0}
            className={`max-w-[400px] 2xl:max-w-[420px] text-center opacity-0 ${
              visibleItems.includes(0) ? 'animate-fade-slide-up' : ''
            }`}
          >
            <h1 ref={headingRef} className="text-[40px] md:text-[50px] xl:text-[60px] font-extrabold text-[#0a2e5d] leading-tight mb-4">
              {typedText}
            </h1>
            <p className="text-[18px] md:text-[20px] lg:text-[22px] italic text-[#4c4c4b] leading-[1.6] m-0">
              <strong>Big Blue Inc. (BBI)</strong> is an established, fast‐growing and financially sound organization. Extensive footprint of owned offices in Canada.
            </p>
          </div>
        </div>

        {/* Row 1 */}
        <div className="group flex flex-col items-center justify-center p-6 border-t border-b md:border-b-0 lg:border-t-0 lg:border-l border-black transition-all duration-300 hover:bg-[#02044F]">
          <div
            ref={(el) => (contentRefs.current[1] = el)}
            data-index={1}
            className={`max-w-[300px] xl:text-[22px] 2xl:text-[24px] text-center opacity-0 ${visibleItems.includes(1) ? 'animate-fade-slide-up' : ''}`}
          >
            <img src={advantages[0].icon} alt="" className="w-20 h-20 mb-3 mx-auto" />
            <strong className="block font-bold mb-1 text-[#0a2e5d] group-hover:text-white transition-all duration-300">
              {advantages[0].title}
            </strong>
            <p className="text-[#4c4c4b] group-hover:text-white transition-all duration-300">
              {advantages[0].description}
            </p>
          </div>
        </div>

        {/* Row 2 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 grid-rows-2 md:border-t md:border-black lg:border-black">
          {[1, 2, 3, 4].map((idx) => (
            <div key={idx} className="group flex flex-col items-center justify-center p-4 lg:border-l border-b border-black transition-all duration-300 hover:bg-[#02044F]">
              <div
                ref={(el) => (contentRefs.current[idx + 1] = el)}
                data-index={idx + 1}
                className={`max-w-[300px] xl:text-[22px] 2xl:text-[24px] text-center opacity-0 ${visibleItems.includes(idx + 1) ? 'animate-fade-slide-up' : ''}`}
              >
                <img src={advantages[idx].icon} alt="" className="w-20 h-20 mb-2 mx-auto" />
                <strong className="block font-bold mb-1 text-[#0a2e5d] group-hover:text-white transition-all duration-300">
                  {advantages[idx].title}
                </strong>
                <p className="text-[#4c4c4b] group-hover:text-white transition-all duration-300">
                  {advantages[idx].description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Row 3 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 lg:flex border-b border-black">
          {[5, 6].map((idx) => (
            <div
              key={idx}
              className={`group flex-1 flex flex-col items-center justify-center p-4 ${
                idx === 5 ? 'border-b lg:border-b-0 lg:border-l border-black' : 'lg:border-l border-black'
              } transition-all duration-300 hover:bg-[#02044F]`}
            >
              <div
                ref={(el) => (contentRefs.current[idx + 1] = el)}
                data-index={idx + 1}
                className={`max-w-[300px] xl:text-[22px] 2xl:text-[24px] text-center opacity-0 ${visibleItems.includes(idx + 1) ? 'animate-fade-slide-up' : ''}`}
              >
                <img src={advantages[idx].icon} alt="" className="w-20 h-20 mb-2 mx-auto" />
                <strong className="block font-bold mb-1 text-[#0a2e5d] group-hover:text-white transition-all duration-300">
                  {advantages[idx].title}
                </strong>
                <p className="text-[#4c4c4b] group-hover:text-white transition-all duration-300">
                  {advantages[idx].description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
